import { CalendarDate } from "./calendar-date";
export const DEFAULT_TOUR_DURATION_MINUTES = 60;
export const durationOptionsMinutes = [15, 30, 45, 60, 90, 120];
export const buildDurationOptions = (options, currentMinutes) => {
    if (options.includes(currentMinutes)) {
        return options;
    }
    return [...options, currentMinutes].sort((a, b) => a - b);
};
export const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0)
        return `${mins}m`;
    if (mins === 0)
        return `${hours}h`;
    return `${hours}h ${mins}m`;
};
export const TOUR_TIME_GRANULARITY_MINUTES = 5;
export const formatTourDate = (dateValue) => {
    const parsed = CalendarDate.parse(dateValue);
    if (!parsed.hasValue) {
        return null;
    }
    return parsed.get().toDate().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};
export const formatTimeOfDay = (timeStr) => {
    if (!timeStr)
        return "";
    const parts = timeStr.split(":");
    const hour = parseInt(parts[0], 10);
    const minutes = (parts[1] || "00").slice(0, 2);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
};
export const formatTourTime = (timeStr) => {
    const formatted = formatTimeOfDay(timeStr);
    return formatted ? ` at ${formatted}` : "";
};
const ordinalSuffix = (n) => {
    const lastTwo = n % 100;
    if (lastTwo >= 11 && lastTwo <= 13)
        return "th";
    switch (n % 10) {
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
};
export const formatLongDate = (dateValue) => {
    const parsed = CalendarDate.parse(dateValue);
    if (!parsed.hasValue)
        return null;
    const date = parsed.get().toDate();
    const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
    const month = date.toLocaleDateString("en-US", { month: "long" });
    const day = date.getDate();
    return `${weekday}, ${month} ${day}${ordinalSuffix(day)}`;
};
const formatClock = (h, m) => {
    const period = h >= 12 ? "pm" : "am";
    const displayHour = h % 12 === 0 ? 12 : h % 12;
    return `${displayHour}:${String(m).padStart(2, "0")}${period}`;
};
export const formatTimeRange = (timeStr, durationMinutes) => {
    const [hStr, mStr] = timeStr.split(":");
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr ?? "0", 10);
    if (isNaN(h) || isNaN(m))
        return null;
    const startTotal = h * 60 + m;
    const endTotal = startTotal + durationMinutes;
    const endHour = Math.floor(endTotal / 60) % 24;
    const endMin = endTotal % 60;
    return `${formatClock(h, m)} - ${formatClock(endHour, endMin)}`;
};
export const formatEventHeadline = (dateValue, timeStr, durationMinutes) => {
    const date = formatLongDate(dateValue) ?? dateValue;
    if (!timeStr)
        return date;
    const range = formatTimeRange(timeStr, durationMinutes);
    return range ? `${date} ${range}` : date;
};
export const formatDateBlock = (dateValue) => {
    const parsed = CalendarDate.parse(dateValue);
    if (!parsed.hasValue)
        return null;
    const date = parsed.get().toDate();
    return {
        month: date.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
        day: date.getDate(),
        weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
    };
};
const parseTourDate = (dateValue) => {
    const parsed = CalendarDate.parse(dateValue);
    return parsed.hasValue ? parsed.get() : null;
};
const parseTourTime = (timeStr) => {
    if (!timeStr)
        return null;
    const [h, m] = timeStr.split(":").map(Number);
    if (isNaN(h) || isNaN(m))
        return null;
    return { hours: h, minutes: m };
};
const timeIsBeforeNow = (tourTime, nowHours, nowMinutes) => {
    if (tourTime.hours < nowHours)
        return true;
    if (tourTime.hours === nowHours && tourTime.minutes < nowMinutes)
        return true;
    return false;
};
const timeIsAtOrBeforeNow = (tourTime, nowHours, nowMinutes) => {
    if (tourTime.hours < nowHours)
        return true;
    if (tourTime.hours === nowHours && tourTime.minutes <= nowMinutes)
        return true;
    return false;
};
export const isTourDateTimeInPast = (dateValue, timeStr, now) => {
    const tourDate = parseTourDate(dateValue);
    if (!tourDate)
        return false;
    const today = CalendarDate.fromLocalDate(now);
    if (tourDate.lessThan(today))
        return true;
    if (today.lessThan(tourDate))
        return false;
    const time = parseTourTime(timeStr);
    if (!time)
        return false;
    return timeIsAtOrBeforeNow(time, now.getHours(), now.getMinutes());
};
export const splitTours = (tours, now = new Date()) => {
    const today = CalendarDate.fromLocalDate(now);
    const nowHours = now.getHours();
    const nowMinutes = now.getMinutes();
    const upcoming = [];
    const past = [];
    for (const tour of tours) {
        const tourDate = parseTourDate(tour.tourDate);
        if (!tourDate) {
            upcoming.push(tour);
            continue;
        }
        if (tourDate.lessThan(today)) {
            past.push(tour);
        }
        else if (today.lessThan(tourDate)) {
            upcoming.push(tour);
        }
        else {
            const time = parseTourTime(tour.tourTime);
            if (time && timeIsBeforeNow(time, nowHours, nowMinutes)) {
                past.push(tour);
            }
            else {
                upcoming.push(tour);
            }
        }
    }
    const compareAsc = (a, b) => {
        const dateA = parseTourDate(a.tourDate);
        const dateB = parseTourDate(b.tourDate);
        if (!dateA || !dateB)
            return 0;
        if (dateA.lessThan(dateB))
            return -1;
        if (dateB.lessThan(dateA))
            return 1;
        const timeA = parseTourTime(a.tourTime);
        const timeB = parseTourTime(b.tourTime);
        if (!timeA && !timeB)
            return 0;
        if (!timeA)
            return 1;
        if (!timeB)
            return -1;
        if (timeA.hours !== timeB.hours)
            return timeA.hours - timeB.hours;
        return timeA.minutes - timeB.minutes;
    };
    upcoming.sort(compareAsc);
    past.sort((a, b) => -compareAsc(a, b));
    return { upcoming, past };
};
//# sourceMappingURL=tour.js.map